import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  InputLabel,
  TableRow,
  MenuItem,
  FormHelperText ,
  TablePagination,
  FormControl,
  TextField,
  IconButton,
  Select,
  Box,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios'; // Import Axios for making API requests
import Swal from 'sweetalert2';

// Project import
import { gridSpacing } from 'config.js';

// Updated API URLs
const apiUrl = 'https://apidashboard.eurekagroup.id/cabinets';
const addCabinetUrl = `${apiUrl}/add`;
const editCabinetUrl = `${apiUrl}/edit`;
const deleteCabinetUrl = (id) => `${apiUrl}/delete/${id}`;
const officesApiUrl = 'https://apidashboard.eurekagroup.id/office'; // Add this line

const CabinetPage = () => {
  const [data, setData] = useState([]);
  const [offices, setOffices] = useState([]); // State to hold office data
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCabinet, setSelectedCabinet] = useState(null);
  const [cabinetName, setCabinetName] = useState('');
  const [idOffice, setIdOffice] = useState(''); // Change to store selected office ID
  const [isActive, setIsActive] = useState(1);

  // Fetch cabinets and offices data on component mount
  useEffect(() => {
    const fetchCabinets = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data); // Set the fetched data to the state
      } catch (error) {
        console.error('Error fetching cabinets data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOffices = async () => { // Fetch offices
      try {
        const response = await axios.get(officesApiUrl);
        setOffices(response.data); // Set the fetched office data to state
      } catch (error) {
        console.error('Error fetching offices data:', error);
      }
    };

    fetchCabinets();
    fetchOffices();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (cabinet) => {
    if (cabinet) {
      setSelectedCabinet(cabinet);
      setCabinetName(cabinet.cabinet_name);
      setIdOffice(cabinet.id_office); // Set the selected office ID
      setIsActive(cabinet.is_active);
    } else {
      setSelectedCabinet(null);
      setCabinetName('');
      setIdOffice(''); // Reset selected office ID
      setIsActive(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveCabinet = async () => {
    // Validate required fields
    if (!cabinetName || !idOffice) {
      Swal.fire({
        title: 'Error',
        text: 'Please fill in all required fields!',
        icon: 'error',
        confirmButtonText: 'Okay',
      });
      return; // Prevent submission if fields are empty
    }
  
    const cabinetData = { cabinet_name: cabinetName, id_office: idOffice, is_active: isActive };
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    if (selectedCabinet) {
      // Jika mengedit kabinet
      try {
        await axios.put(editCabinetUrl, 
          { ...cabinetData, id: selectedCabinet._id },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Tambahkan header Authorization
            },
          }
        );
        setData(data.map(emp => (emp._id === selectedCabinet._id ? { ...emp, ...cabinetData } : emp)));
        setMessage('Cabinet updated successfully');
      } catch (error) {
        console.error('Error updating cabinet:', error.response?.data || error.message);
        setMessage(error.response?.data?.error || 'Error updating cabinet');
        setSuccessPopup(true);
      }
    } else {
      // Jika menambahkan kabinet baru
      try {
        const response = await axios.post(addCabinetUrl, cabinetData, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData([...data, response.data]);
        setMessage('Cabinet added successfully');
      } catch (error) {
        console.error('Error adding cabinet:', error.response?.data || error.message);
        setMessage(error.response?.data?.error || 'Error adding cabinet');
        setSuccessPopup(true);
      }
    }
  
    handleCloseDialog();
  };  
  
  useEffect(() => {
    if (selectedCabinet) {
      console.log('Selected Cabinet:', selectedCabinet);
      setCabinetName(selectedCabinet.cabinet_name);
      setIdOffice(selectedCabinet.id_office._id); // Ensure this is set correctly
      setIsActive(selectedCabinet.is_active);
    } else {
      setCabinetName('');
      setIdOffice('');
      setIsActive(1);
    }
  }, [selectedCabinet]);

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
      try {
        await axios.delete(deleteCabinetUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        }); // Panggil API DELETE dengan token
        setData(data.filter(cabinet => cabinet._id !== id));
        setMessage('Cabinet deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting cabinet:', error.response?.data || error.message);
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.error || 'Failed to delete cabinet',
          icon: 'error',
          confirmButtonText: 'Okay',
        });
      }
    }
  };  

  const filteredData = data.filter(cabinet =>
    cabinet.cabinet_name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <FolderIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Kabinet
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data kabinet dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Kabinet
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Kabinet"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Kabinet
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data kabinet...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Kabinet
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kantor
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((cabinet, index) => (
                      <TableRow 
                        key={cabinet._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {cabinet.cabinet_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {cabinet.id_office?.office_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: cabinet.is_active === 1 ? 'success.light' : 'error.light',
                            color: cabinet.is_active === 1 ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {cabinet.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(cabinet)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(cabinet._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>

      {/* Dialog for adding/editing cabinet */}
      <Dialog open={openDialog} onClose={() => {} }>
        <DialogTitle>{selectedCabinet ? 'Edit Cabinet' : 'Add Cabinet'}</DialogTitle>
        <DialogContent>
        <TextField
  autoFocus
  margin="dense"
  label="Cabinet Name"
  type="text"
  fullWidth
  variant="outlined"
  value={cabinetName}
  onChange={(e) => setCabinetName(e.target.value)}
  error={!cabinetName} // Add error style if cabinetName is empty
  helperText={!cabinetName ? 'Cabinet Name is required' : ''} // Show helper text if cabinetName is empty
/>
<FormControl fullWidth margin="dense" error={!idOffice}>
  <InputLabel id="id-office-label">ID Office</InputLabel>
  <Select
    label="Office"
    value={idOffice || ''} // Make sure the value is either the id of office or an empty string
    onChange={(e) => setIdOffice(e.target.value)}
  >
    {offices.map((office) => (
      <MenuItem key={office._id} value={office._id}>
        {office.office_name}
      </MenuItem>
    ))}
  </Select>
  {!idOffice && <FormHelperText>Office is required</FormHelperText>}
</FormControl>
<FormControl margin="dense" error={isActive === ''}>
  <InputLabel id="is-active-label">Status</InputLabel>
  <Select
    labelId="is-active-label"
    value={isActive}
    onChange={(e) => setIsActive(e.target.value)}
  >
    <MenuItem value={1}>Active</MenuItem>
    <MenuItem value={0}>Inactive</MenuItem>
  </Select>
  {isActive === '' && <FormHelperText>Status is required</FormHelperText>} {/* Helper text for error */}
</FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveCabinet} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success messages */}
      <Snackbar
        open={successPopup}
        autoHideDuration={6000}
        onClose={() => setSuccessPopup(false)}
        message={message}
      />
    </>
  );
};

export default CabinetPage;
